import { useCallback } from "react";
import { generateAllInsights as generateAllInsightsAggregator } from "./generateAllInsights";
import { generateProfileOverview as generateProfileOverviewHook } from "./generateProfileOverview";
import { generateIndividualInsights as generateIndividualInsightsHook } from "./generateIndividualInsights";

export function useGenerateAllInsights({
  mountedRef,
  getters,
  setters,
  context,
}) {
  const { getAllQuizData, getProfileOverview, getQuizInsights } = getters;
  const { setLoading, setError, setProfileOverview, setQuizInsights } = setters;
  const { displayName, userId } = context;

  const generateAllInsights = useCallback(
    async (quizDataOverride = null, force = false) => {
      if (!mountedRef.current) {
        console.log("⚠️ generateAllInsights: Component unmounted, skipping");
        return;
      }

      const currentQuizData = quizDataOverride || getAllQuizData();

      if (!currentQuizData || currentQuizData.length === 0) {
        console.error("❌ generateAllInsights: No quiz data available");
        if (mountedRef.current) {
          setError("No completed quiz data available to analyze");
        }
        return;
      }

      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const { overview, individual } = await generateAllInsightsAggregator(
          currentQuizData,
          force,
          getProfileOverview(),
          getQuizInsights(),
          (qd) =>
            generateProfileOverviewHook(
              qd,
              displayName || "this person",
              userId
            ),
          (qd) =>
            generateIndividualInsightsHook(qd, displayName || "this person")
        );

        if (mountedRef.current) {
          setProfileOverview(overview);
          setQuizInsights(individual);
        }
      } catch (err) {
        console.error("❌ Error in generateAllInsights:", err);
        if (mountedRef.current) {
          setError(err.message || "Unknown error occurred");
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [
      mountedRef,
      getAllQuizData,
      getProfileOverview,
      getQuizInsights,
      setLoading,
      setError,
      setProfileOverview,
      setQuizInsights,
      displayName,
      userId,
    ]
  );

  return generateAllInsights;
}
